/**
 * Lead Volume — LegalFlare first, then each Originating Firm (#1051, Firm
 * Success PRD 2.2 and 2.3's look).
 *
 * Every number on the page is a kept run's (GET /api/firm-success/lead-volume/
 * page), shown with its as_of. Each chart has its own vertical axis — Firms
 * never share one (2.2.6). The main line is the rolling 7-day sum (2.2.7); the
 * second is the rolling 28-day sum ÷ 4, in the same per-week units (2.2.8);
 * the daily counts are faint bars behind them (2.2.9). Above each chart, plain
 * numbers (2.2.10). Firms are ordered by total (2.2.11).
 *
 * The strip under each chart marks days unusual FOR THAT SCOPE under the
 * settings in force (2.3): amber low, deep amber low several days running,
 * blue high, teal growth, slate quiet longer than ever. No colour here means
 * good or bad, healthy or at risk; no red (the surface's palette rule, fs.css).
 */

import { useState } from "react";
import type { LeadVolumePageResponse, LeadVolumeRank, LeadVolumeSeries } from "@command-center/shared";
import { ClientSuccessNav } from "@/components/client-success-nav";
import { useLeadVolumePage } from "@/lib/use-firm-success";
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
/** A Pacific day label with its year: the charts span years. */
function fmtDate(iso: string): string {
  return `${MONTHS[Number(iso.slice(5, 7)) - 1]} ${Number(iso.slice(8, 10))}, ${iso.slice(0, 4)}`;
}
import "./fs.css";

const RANK_COLOR: Partial<Record<LeadVolumeRank, string>> = {
  low: "hsl(42 92% 62%)",
  low_persisting: "hsl(28 88% 42%)",
  high: "hsl(210 80% 52%)",
  growth: "hsl(174 70% 33%)",
};
const QUIET_COLOR = "hsl(230 15% 45%)";
const RANK_WORDS: Record<LeadVolumeRank, string> = {
  none: "within its own range",
  low: "unusually low for itself",
  low_persisting: "unusually low, several days running",
  high: "unusually high for itself",
  growth: "growth — held above its own range",
  not_enough_history: "not enough history yet",
  too_small_to_rank: "too small to rank; the quiet rule speaks for it",
};

function addDays(iso: string, n: number): string {
  const t = Date.parse(`${iso}T12:00:00Z`) + n * 86_400_000;
  // day-boundary: date arithmetic on a date-only label; no clock
  return new Date(t).toISOString().slice(0, 10);
}

export function LeadVolumePage() {
  const page = useLeadVolumePage();
  return (
    <div className="p-6">
      <ClientSuccessNav active="lead-volume" />
      <div className="fs-surface" style={{ margin: "-8px -24px 0", borderRadius: 12 }}>
        <div className="shell">
          <h1>Lead Volume</h1>
          {page.isLoading ? <p className="soft">Loading…</p> : null}
          {page.isError ? <p className="soft">Could not load Lead Volume: {page.error.message}</p> : null}
          {page.data !== undefined ? <Body data={page.data} /> : null}
        </div>
      </div>
    </div>
  );
}

function Body({ data }: { data: LeadVolumePageResponse }) {
  const r = data.run;
  const ch = r.checks;
  return (
    <>
      <p className="sub" data-testid="lv-asof">
        As of <b>{r.asOf}</b> (run {r.runId}, {r.trigger === "scheduled" ? "the daily run" : `counted by ${r.triggeredBy}`}).
        {" "}{r.rowsRead} Leads rows read; {r.rowsWithNoDay} with no Creation Date are not counted; {r.restampedUtcToPacific} counted
        on the Pacific day where the UTC day was stamped. Checks: each Firm's days sum to its total{" "}
        <b>{ch.firmDailySumEqualsTotal.equal ? "equal" : `differ for ${ch.firmDailySumEqualsTotal.firmsWhereDiffer.length}`}</b>; Leads +
        Transfers = Referrals <b>{ch.leadsPlusTransfersEqualsReferrals.equal ? "equal" : "differ"}</b>; Firms + no Firm ={" "}
        {ch.firmsPlusNoFirmEqualsSystem.firmsSum} + {ch.firmsPlusNoFirmEqualsSystem.noFirm} against{" "}
        {ch.firmsPlusNoFirmEqualsSystem.rowsWithADay} <b>{ch.firmsPlusNoFirmEqualsSystem.equal ? "equal" : "differ"}</b>.
      </p>
      <Legend version={data.settings.version} />

      <section className="lv-card" data-testid="lv-legalflare">
        <h2>LegalFlare</h2>
        <Numbers n={data.legalflare.numbers} today={data.today} />
        <Chart series={data.series.legalflare} label="Referrals" />
        <h3>The five largest Originating Firms against all others</h3>
        <p className="soft">
          {data.legalflare.topFive.map((f) => f.name).join(", ")} — against every other Originating Firm and the Referrals
          with no Originating Firm. Rolling 28-day sums ÷ 4.
        </p>
        <TopFive top={data.series.topFive} />
        <h3>
          Originating Firms sending — {data.legalflare.firmsSendingNow} today
        </h3>
        <p className="soft">A Firm is sending when it has a Referral in the 28 days ending that day.</p>
        <Chart series={data.series.firmsSending} label="Firms sending" level />
        <StartedStopped data={data} />
      </section>

      {data.series.firms.map((s, i) => {
        const f = data.firms[i]!;
        return (
          <section className="lv-card" key={s.scope} data-testid={`lv-firm-${s.scope}`}>
            <h2>{f.name || s.scope}</h2>
            <Numbers n={f.numbers} today={data.today} />
            <Chart series={s} label="Referrals" />
          </section>
        );
      })}
      {data.noOriginatingFirm !== null ? (
        <p className="soft">
          Referrals with no Originating Firm: {data.noOriginatingFirm.referrals} (in LegalFlare's total), last on{" "}
          {fmtDate(data.noOriginatingFirm.lastDay)}.
        </p>
      ) : null}
    </>
  );
}

function Legend({ version }: { version: number }) {
  return (
    <div className="glegend" data-testid="lv-legend">
      <span className="k"><i style={{ background: "hsl(16 85% 48%)" }} />Rolling 7-day sum</span>
      <span className="k"><i style={{ background: "hsl(230 45% 25%)", height: 0, borderTop: "3px dashed hsl(230 45% 25%)" }} />Rolling 28-day sum ÷ 4</span>
      {(["low", "low_persisting", "high", "growth"] as const).map((k) => (
        <span className="k" key={k}><i style={{ background: RANK_COLOR[k] }} />{RANK_WORDS[k]}</span>
      ))}
      <span className="k"><i style={{ background: QUIET_COLOR }} />quiet longer than it ever has been</span>
      <span className="soft">Unusual for that scope only, under settings version {version}. No colour means good or bad.</span>
    </div>
  );
}

function Numbers({ n, today }: { n: LeadVolumePageResponse["legalflare"]["numbers"]; today: string }) {
  const cell = (label: string, v: string | number) => (
    <div className="lv-num" key={label}>
      <div className="soft">{label}</div>
      <b className="num">{v}</b>
    </div>
  );
  return (
    <div className="lv-nums" data-testid="lv-numbers">
      {cell("Yesterday", n.yesterday)}
      {cell("Last 7 days", n.last7)}
      {cell("The 7 before", n.prior7)}
      {cell("Last 28 days", n.last28)}
      {cell("The 28 before", n.prior28)}
      {cell("Total to date", n.total)}
      {cell("Last Referral", n.lastDay === null ? "—" : fmtDate(n.lastDay))}
      {cell("Days since", n.daysSinceLast === null ? "—" : n.daysSinceLast)}
      {cell("Leads / Transfers", `${n.leads} / ${n.transfers}`)}
      {cell("Today so far", n.today)}
      <span className="soft" style={{ alignSelf: "end" }}>Windows end yesterday, {fmtDate(addDays(today, -1))}; Today so far and Total to date run to the as-of time above.</span>
    </div>
  );
}

function axisTop(max: number): { top: number; step: number } {
  const step = Math.max(1, Math.ceil(max / 4));
  return { top: Math.max(step, Math.ceil((max * 1.15) / step) * step), step };
}

/** One scope's chart, on its own axis. `level` draws a count of Firms (no
 *  bars: the value is not a daily count). */
function Chart({ series, label, level = false }: { series: LeadVolumeSeries; label: string; level?: boolean }) {
  const [hover, setHover] = useState<number | null>(null);
  const N = series.n.length;
  if (N === 0) return null;
  const W = 960;
  const H = 210;
  const L = 40;
  const R = 10;
  const T = 10;
  const B = 40;
  const iw = W - L - R;
  const ih = H - T - B - 10; // 10px strip below
  let max = 1;
  for (let i = 0; i < N; i++) {
    max = Math.max(max, series.line7[i] ?? 0, series.line28[i] ?? 0, level ? 0 : series.n[i]!);
  }
  const { top, step } = axisTop(max);
  const bw = iw / N;
  const x = (i: number) => L + i * bw + bw / 2;
  const y = (v: number) => T + ih - (v / top) * ih;
  const path = (xs: (number | null)[]) => {
    let d = "";
    let pen = false;
    xs.forEach((v, i) => {
      if (v === null) {
        pen = false;
        return;
      }
      d += `${pen ? "L" : "M"}${x(i).toFixed(1)} ${y(v).toFixed(1)} `;
      pen = true;
    });
    return d;
  };
  const grid = [];
  for (let v = 0; v <= top; v += step) {
    grid.push(
      <g key={v}>
        <line x1={L} x2={W - R} y1={y(v)} y2={y(v)} stroke="hsl(38 30% 90%)" />
        <text x={L - 6} y={y(v) + 3.5} textAnchor="end" fontSize={10} fill="hsl(230 20% 40%)">{v}</text>
      </g>,
    );
  }
  // Month ticks.
  const ticks = [];
  for (let i = 0; i < N; i++) {
    const d = addDays(series.start, i);
    if (d.endsWith("-01")) {
      ticks.push(
        <text key={d} x={x(i)} y={H - 8} textAnchor="middle" fontSize={9.5} fill="hsl(230 20% 40%)">
          {d.slice(0, 7)}
        </text>,
      );
    }
  }
  const stripY = T + ih + 6;
  const h = hover;
  return (
    <div className="graph" data-testid="lv-chart">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label={`${series.name}: ${label} per day from ${series.start}`}
        onMouseLeave={() => setHover(null)}
        onMouseMove={(e) => {
          const box = (e.currentTarget as SVGSVGElement).getBoundingClientRect();
          const px = ((e.clientX - box.left) / box.width) * W;
          const i = Math.floor((px - L) / bw);
          setHover(i >= 0 && i < N ? i : null);
        }}
      >
        {grid}
        {level
          ? null
          : series.n.map((v, i) =>
              v > 0 ? <rect key={i} x={L + i * bw} y={y(v)} width={Math.max(bw - 0.3, 0.6)} height={T + ih - y(v)} fill="hsl(38 40% 78%)" /> : null,
            )}
        <path d={path(series.line28)} fill="none" stroke="hsl(230 45% 25%)" strokeWidth={1.4} strokeDasharray="5 3" />
        <path d={path(series.line7)} fill="none" stroke="hsl(16 85% 48%)" strokeWidth={1.8} />
        {series.rank.map((rk, i) => {
          const c = RANK_COLOR[rk];
          return c ? <rect key={`s${i}`} x={L + i * bw} y={stripY} width={Math.max(bw, 0.8)} height={6} fill={c} /> : null;
        })}
        {series.quiet.map((q, i) => (q ? <rect key={`q${i}`} x={L + i * bw} y={stripY + 7} width={Math.max(bw, 0.8)} height={3} fill={QUIET_COLOR} /> : null))}
        {ticks}
        {h !== null ? <line x1={x(h)} x2={x(h)} y1={T} y2={T + ih} stroke="hsl(230 20% 50%)" strokeWidth={0.8} /> : null}
      </svg>
      <div className="soft" data-testid="lv-hover" style={{ minHeight: 20 }}>
        {h !== null
          ? `${fmtDate(addDays(series.start, h))}: ${series.n[h]} ${label.toLowerCase()}${
              series.line7[h] !== null && series.line7[h] !== undefined ? ` · 7-day ${series.line7[h]}` : ""
            }${series.line28[h] !== null && series.line28[h] !== undefined ? ` · 28-day ÷ 4 ${Number(series.line28[h]).toFixed(2)}` : ""}${
              h < series.rank.length ? ` · ${RANK_WORDS[series.rank[h]!]}` : ""
            }${series.quiet[h] ? " · quiet longer than ever" : ""}`
          : `From ${fmtDate(series.start)}. Axis to ${top}, this scope's own.`}
      </div>
    </div>
  );
}

function TopFive({ top }: { top: LeadVolumePageResponse["series"]["topFive"] }) {
  const N = top.topN.length;
  if (N === 0) return null;
  const roll = (xs: number[]) => {
    const out: (number | null)[] = [];
    let s = 0;
    for (let i = 0; i < xs.length; i++) {
      s += xs[i]!;
      if (i >= 28) s -= xs[i - 28]!;
      out.push(i >= 27 ? s / 4 : null);
    }
    return out;
  };
  const a = roll(top.topN);
  const b = roll(top.othersN);
  const W = 960;
  const H = 170;
  const L = 40;
  const R = 10;
  const T = 10;
  const B = 24;
  const iw = W - L - R;
  const ih = H - T - B;
  let max = 1;
  for (let i = 0; i < N; i++) max = Math.max(max, (a[i] ?? 0) + (b[i] ?? 0));
  const { top: yTop, step } = axisTop(max);
  const x = (i: number) => L + (i * iw) / Math.max(1, N - 1);
  const y = (v: number) => T + ih - (v / yTop) * ih;
  const area = (lo: (i: number) => number, hi: (i: number) => number) => {
    const idx = [...Array(N).keys()].filter((i) => a[i] !== null);
    if (idx.length === 0) return "";
    const up = idx.map((i) => `${x(i).toFixed(1)} ${y(hi(i)).toFixed(1)}`).join(" L");
    const down = [...idx].reverse().map((i) => `${x(i).toFixed(1)} ${y(lo(i)).toFixed(1)}`).join(" L");
    return `M${up} L${down} Z`;
  };
  const grid = [];
  for (let v = 0; v <= yTop; v += step) {
    grid.push(
      <g key={v}>
        <line x1={L} x2={W - R} y1={y(v)} y2={y(v)} stroke="hsl(38 30% 90%)" />
        <text x={L - 6} y={y(v) + 3.5} textAnchor="end" fontSize={10} fill="hsl(230 20% 40%)">{v}</text>
      </g>,
    );
  }
  return (
    <div className="graph" data-testid="lv-topfive">
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="the five largest Originating Firms against all others">
        {grid}
        <path d={area(() => 0, (i) => a[i] ?? 0)} fill="hsl(268 60% 55% / 0.55)" />
        <path d={area((i) => a[i] ?? 0, (i) => (a[i] ?? 0) + (b[i] ?? 0))} fill="hsl(150 55% 33% / 0.45)" />
      </svg>
      <div className="glegend">
        <span className="k"><i style={{ background: "hsl(268 60% 55%)" }} />The five largest</span>
        <span className="k"><i style={{ background: "hsl(150 55% 33%)" }} />All others</span>
      </div>
    </div>
  );
}

function StartedStopped({ data }: { data: LeadVolumePageResponse }) {
  const { started, stopped } = data.legalflare;
  return (
    <div className="lv-two" data-testid="lv-started-stopped">
      <div>
        <h3>Firms that started</h3>
        <ul className="lv-list">
          {started.map((f) => (
            <li key={f.firmId}>
              {fmtDate(f.firstDay)} — {f.name || f.firmId}
            </li>
          ))}
        </ul>
      </div>
      <div>
        <h3>Firms that stopped — no Referral in the last 28 days</h3>
        <ul className="lv-list">
          {stopped.length === 0 ? <li className="soft">None.</li> : null}
          {stopped.map((f) => (
            <li key={f.firmId}>
              {f.name || f.firmId} — last {fmtDate(f.lastDay)}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
